import { useEffect, useRef, useState } from 'preact/hooks';
import type { Action, BotKind, GameView } from '../engine';
import { CHAT_HISTORY, PING, type ChatMessage, type ClientMessage, type GameSettings, type RoomSnapshot, type ServerMessage } from '../shared/protocol';
import type { LogEntry } from './game-client';
import { logEntry } from './log';

const TOKENS_KEY = 'hexagon.seats';
const PING_MS = 25_000;
// The runtime answers pings without waking the room, so a missing pong means
// the socket is dead (common after a phone sleeps) even if it still reads OPEN.
const PONG_TIMEOUT_MS = 8_000;
// Safety net in case a reply never arrives; the server always answers an action.
const PENDING_TIMEOUT_MS = 8_000;
const MAX_BACKOFF_MS = 10_000;

export function roomSocketUrl(code: string): string {
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${location.host}/api/rooms/${code}/ws`;
}

function readTokens(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(TOKENS_KEY) ?? '{}') as Record<string, string>;
  } catch {
    return {};
  }
}

export function seatToken(code: string): string | undefined {
  return readTokens()[code];
}

function storeToken(code: string, token: string | null): void {
  try {
    const tokens = readTokens();
    if (token) tokens[code] = token;
    else delete tokens[code];
    localStorage.setItem(TOKENS_KEY, JSON.stringify(tokens));
  } catch {
    // Private mode: the seat just won't survive closing the tab.
  }
}

export type ConnectionStatus = 'connecting' | 'open' | 'reconnecting' | 'closed';

/**
 * A room socket that reconnects with backoff and re-sends `hello` (with the
 * stored seat token) every time it opens. Phones drop sockets whenever the
 * browser is backgrounded, so reconnecting is the normal path, not an edge case.
 */
export class RoomConnection {
  private ws: WebSocket | null = null;
  private attempt = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private pongTimer: ReturnType<typeof setTimeout> | null = null;
  private stopped = false;

  constructor(
    private readonly code: string,
    private readonly onMessage: (msg: ServerMessage) => void,
    private readonly onStatus: (status: ConnectionStatus) => void,
  ) {
    document.addEventListener('visibilitychange', this.onVisible);
    this.open();
  }

  private reportVisibility(): void {
    this.send({ type: 'visible', visible: document.visibilityState === 'visible' });
  }

  private onVisible = () => {
    if (this.ws?.readyState === WebSocket.OPEN) this.reportVisibility();
    if (document.visibilityState !== 'visible' || this.stopped) return;
    if (this.ws?.readyState === WebSocket.OPEN) {
      // Back from the background: check the socket survived instead of trusting readyState.
      this.ping(this.ws);
    } else {
      this.attempt = 0;
      this.open();
    }
  };

  private ping(ws: WebSocket): void {
    if (ws.readyState !== WebSocket.OPEN) return;
    ws.send(PING);
    if (this.pongTimer) return;
    this.pongTimer = setTimeout(() => {
      this.pongTimer = null;
      if (this.ws === ws) this.drop(ws);
    }, PONG_TIMEOUT_MS);
  }

  /** Abandons a dead socket and reconnects now; its close event may never arrive. */
  private drop(ws: WebSocket): void {
    this.clearTimers();
    this.ws = null;
    try {
      ws.close();
    } catch {
      // Already closing.
    }
    if (this.stopped) return;
    this.attempt = 1;
    this.onStatus('reconnecting');
    this.open();
  }

  private clearTimers(): void {
    if (this.pingTimer) clearInterval(this.pingTimer);
    if (this.pongTimer) clearTimeout(this.pongTimer);
    this.pingTimer = null;
    this.pongTimer = null;
  }

  private open(): void {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    if (this.ws && this.ws.readyState <= WebSocket.OPEN) return;
    this.onStatus(this.attempt === 0 ? 'connecting' : 'reconnecting');
    const ws = new WebSocket(roomSocketUrl(this.code));
    this.ws = ws;
    ws.addEventListener('open', () => {
      this.attempt = 0;
      this.onStatus('open');
      const token = seatToken(this.code);
      this.send({ type: 'hello', ...(token ? { token } : {}) });
      this.reportVisibility();
      this.pingTimer = setInterval(() => this.ping(ws), PING_MS);
    });
    ws.addEventListener('message', (e) => {
      if (typeof e.data !== 'string') return;
      if (this.ws === ws && this.pongTimer) {
        // Any traffic proves the socket is alive, not just the pong itself.
        clearTimeout(this.pongTimer);
        this.pongTimer = null;
      }
      if (e.data === 'pong') return;
      let msg: ServerMessage;
      try {
        msg = JSON.parse(e.data) as ServerMessage;
      } catch {
        return;
      }
      if (msg.type === 'welcome') {
        if (msg.token) storeToken(this.code, msg.token);
        else if (msg.seat === null) storeToken(this.code, null);
      }
      if (msg.type === 'error' && msg.fatal) this.stop();
      this.onMessage(msg);
    });
    ws.addEventListener('close', () => {
      if (this.ws !== ws) return;
      this.clearTimers();
      this.ws = null;
      if (this.stopped) return this.onStatus('closed');
      this.attempt++;
      this.onStatus('reconnecting');
      const delay = Math.min(MAX_BACKOFF_MS, 500 * 2 ** (this.attempt - 1));
      this.retryTimer = setTimeout(() => this.open(), delay);
    });
  }

  send(msg: ClientMessage): boolean {
    if (this.ws?.readyState !== WebSocket.OPEN) return false;
    this.ws.send(JSON.stringify(msg));
    return true;
  }

  stop(): void {
    this.stopped = true;
    document.removeEventListener('visibilitychange', this.onVisible);
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.clearTimers();
    this.ws?.close(1000, 'leaving');
  }
}

export interface RoomState {
  status: ConnectionStatus;
  seat: number | null;
  room: RoomSnapshot | null;
  game: { view: GameView; legal: Action[] } | null;
  log: LogEntry[];
  /** Latest server error, with an id so repeated identical errors still show. */
  notice: { id: number; text: string } | null;
  /** Latest informational message (e.g. an undo), same id scheme as `notice`. */
  info: { id: number; text: string } | null;
  fatal: string | null;
  /** An action was sent and its reply (new view or error) hasn't arrived yet. */
  pending: boolean;
  chat: ChatMessage[];
}

export interface RoomControls {
  join(name: string): void;
  leave(): void;
  /** Host: update the lobby settings everyone sees. */
  configure(settings: GameSettings): void;
  start(settings: GameSettings): void;
  continueTo(target: number): void;
  backToLobby(): void;
  say(text: string): void;
  addCpu(): void;
  setCpu(seat: number, kind: BotKind): void;
  setColor(color: number, seat?: number): void;
  cpuTakeover(seat: number): void;
  pushSubscribe(subscription: unknown): void;
  pushUnsubscribe(endpoint: string): void;
  act(action: Action): void;
  undo(): void;
  release(seat: number): void;
  claim(seat: number): void;
}

export function useRoom(code: string): RoomState & RoomControls {
  const [state, setState] = useState<RoomState>({
    status: 'connecting',
    seat: null,
    room: null,
    game: null,
    log: [],
    notice: null,
    info: null,
    fatal: null,
    pending: false,
    chat: [],
  });
  const conn = useRef<RoomConnection | null>(null);
  const ids = useRef(0);
  const pendingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Mirrors `pending` synchronously so two taps in one frame can't both send.
  const inFlight = useRef(false);

  useEffect(() => {
    const onMessage = (msg: ServerMessage) => {
      if (msg.type === 'game' || msg.type === 'error') inFlight.current = false;
      setState((s) => {
        switch (msg.type) {
          case 'welcome':
            return { ...s, seat: msg.seat };
          case 'room':
            return { ...s, room: msg.room };
          case 'game': {
            const names = s.room?.seats.map((x) => x.name) ?? [];
            const lines = msg.events.flatMap((e) => {
              const entry = logEntry(e, names, ids.current + 1);
              if (!entry) return [];
              ids.current++;
              return [entry];
            });
            const log = msg.reset ? lines : [...s.log, ...lines];
            return { ...s, game: { view: msg.view, legal: msg.legal }, log, pending: false };
          }
          case 'info':
            return { ...s, info: { id: ++ids.current, text: msg.message } };
          case 'chat': {
            // History arrives on every (re)connect; dedupe by id so nothing doubles up.
            const merged = msg.reset ? msg.messages : [...s.chat, ...msg.messages.filter((m) => !s.chat.some((c) => c.id === m.id))];
            return { ...s, chat: merged.slice(-CHAT_HISTORY) };
          }
          case 'error':
            return msg.fatal
              ? { ...s, fatal: msg.message, pending: false }
              : { ...s, notice: { id: ++ids.current, text: msg.message }, pending: false };
        }
      });
    };
    const c = new RoomConnection(code, onMessage, (status) => {
      // A reconnect resends hello and gets a fresh view; nothing is still in flight.
      if (status !== 'open') inFlight.current = false;
      setState((s) => ({ ...s, status, pending: status === 'open' ? s.pending : false }));
    });
    conn.current = c;
    return () => c.stop();
  }, [code]);

  const send = (msg: ClientMessage): boolean => {
    if (conn.current?.send(msg)) return true;
    setState((s) => ({ ...s, notice: { id: ++ids.current, text: 'Not connected — try again in a moment.' } }));
    return false;
  };

  return {
    ...state,
    join: (name) => send({ type: 'join', name }),
    leave: () => send({ type: 'leave' }),
    configure: (settings) => send({ type: 'configure', settings }),
    start: (settings) => send({ type: 'start', settings }),
    continueTo: (target) => send({ type: 'continue', target }),
    backToLobby: () => send({ type: 'lobby' }),
    say: (text) => send({ type: 'chat', text }),
    addCpu: () => send({ type: 'addCpu' }),
    setCpu: (seat, kind) => send({ type: 'setCpu', seat, kind }),
    setColor: (color, seat) => send({ type: 'setColor', color, ...(seat === undefined ? {} : { seat }) }),
    cpuTakeover: (seat) => send({ type: 'cpuTakeover', seat }),
    // Quietly skipped while disconnected; it's re-sent on the next connect.
    pushSubscribe: (subscription) => void conn.current?.send({ type: 'pushSubscribe', subscription }),
    pushUnsubscribe: (endpoint) => void conn.current?.send({ type: 'pushUnsubscribe', endpoint }),
    act: (action) => {
      if (inFlight.current || !send({ type: 'action', action })) return;
      inFlight.current = true;
      setState((s) => ({ ...s, pending: true }));
      if (pendingTimer.current) clearTimeout(pendingTimer.current);
      pendingTimer.current = setTimeout(() => {
        inFlight.current = false;
        setState((s) => ({ ...s, pending: false }));
      }, PENDING_TIMEOUT_MS);
    },
    undo: () => send({ type: 'undo' }),
    release: (seat) => send({ type: 'release', seat }),
    claim: (seat) => send({ type: 'claim', seat }),
  };
}
